import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';

import { useInterval } from '../../../core/util';
import { isTerminalJobStatus } from '../../../core/jobs';
import { fetchDeepSEAJobStatus } from '../../../actions/DeepSEAActions';
import JobStatusTable, { Job } from './JobStatusTable';

interface RootState {
  deepsea: {
    selectedJob: Job;
  };
}

const DeepSEAJobStatus: React.FC = () => {
  const selectedJob = useSelector(
    (state: RootState) => state.deepsea.selectedJob,
  );
  const dispatch = useDispatch<
    ThunkDispatch<RootState, { fetch: typeof fetch }, AnyAction>
  >();
  const jobId = selectedJob?.job_id;
  const activeJobId = useRef<string>();
  const [hasPollFailed, setHasPollFailed] = useState(false);
  // Browsers may still contain this transient status from failed HTTP polls.
  const isTerminal =
    selectedJob?.status !== 'error: unable to retrieve job' &&
    isTerminalJobStatus(selectedJob?.status);
  const checks = useRef<Record<string, { storedJob: Job; checkedAt?: number }>>(
    {},
  );
  if (jobId && !checks.current[jobId]) {
    checks.current[jobId] = { storedJob: selectedJob };
  }

  // A stored active status stays unverified until a poll replaces its record.
  const isChecking =
    !isTerminal && checks.current[jobId]?.storedJob === selectedJob;
  const refresh = useCallback(() => {
    if (!jobId || isTerminal) return;
    const check = checks.current[jobId];
    const now = Date.now();
    // Stay below the two-second interval so a late tick cannot suppress the next.
    if (check.checkedAt !== undefined && now - check.checkedAt < 1000) return;
    check.checkedAt = now;
    dispatch(fetchDeepSEAJobStatus(jobId))
      .then(isSuccessful => {
        // Late responses must not change the notice after selection or unmount.
        if (activeJobId.current === jobId) setHasPollFailed(!isSuccessful);
      })
      .catch(() => {
        if (activeJobId.current === jobId) setHasPollFailed(true);
      });
  }, [jobId, isTerminal, dispatch]);

  useEffect(() => {
    activeJobId.current = jobId;
    setHasPollFailed(false);
    refresh();
    return () => {
      activeJobId.current = undefined;
    };
  }, [jobId, refresh]);
  useInterval(refresh, jobId && !isTerminal ? 2000 : null);

  return (
    <JobStatusTable
      job={selectedJob}
      isChecking={isChecking}
      hasPollFailed={hasPollFailed}
    />
  );
};

export default DeepSEAJobStatus;
